<!--
  R16b · Maya's thinking trace, variant A ("one line + a two-level chain"). PT picked this shape
  from `maya_trace_variants.html`; the class names, the token values and the animation timings here
  are that mockup's, ported verbatim.

  This component is the ONLY progress control in the chat. Before R16b a turn showed four at once —
  a 3×3 bouncing-dot capsule, the R16 trace header, a self-opening Reasoning drawer and a "Maya is
  working…" status line under the bubble. All four are now this:

    running   ✦ Reading Acme's latest diagnosis        (one line, the words shimmer, no chevron)
    settled   ✦ Thought for 42s · 5 lookups        ⌄   (one line, a real door)
    opened        · Read the diagnosis · Sep 10   0.1s
                  · Read 2 report sections ⌄      0.2s (a group: same kind, back to back)
                  · Thinking                       11s (every gap, summed; reasoning hides in here)
                  · Wrote the answer               17s

  Since 2026-09-13 the chain is written WHILE she works, not after (PT: "先想思考链，告诉你一步一步
  做什么，最终给答案") — each row lands as the frame that proves it arrives, its timer running, and
  the answer streams in underneath a chain that never folds or disappears:

    live      ✦ Thinking…                          (header = the phase, rows = the steps)
                  · Thinking                       4.6s   ← frozen when the tool started
                  · Reading the latest diagnosis   0.1s
                  · Thinking                       1.5s   ← ticking, shimmering
                  · Writing the answer             2.3s   ← from the first answer token
    settled   ✦ Thought for 14s · 1 lookup    ⌃    (same rows, still open, timers stopped)

  Two things it deliberately does NOT do. It shows no chain at all for a turn that has used no
  tools yet — one shimmering line, as before, because "Thinking" twice over is not two facts. And
  it renders NOTHING for a settled turn with zero tool calls: most production turns use no tools at
  all, and "Thought for 3s · 0 lookups" is a progress control announcing that there was no progress
  to report.

  The artefacts Maya opened or produced are not in here either — they hang under the answer as
  chips (MayaTraceChips.vue), because a report the customer may want tomorrow must not disappear
  when the chain folds.
-->
<template>
  <div v-if="visible" class="mtrace" :class="{ on: open, live }">
    <button
      v-if="!live"
      type="button"
      class="mtr-h"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <svg class="mtr-star" viewBox="0 0 24 24" aria-hidden="true"><path :d="STAR" /></svg>
      <span class="mtr-t done">{{ heading }}</span>
      <svg class="mtr-chev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
    </button>
    <div v-else class="mtr-h mtr-h-live" role="status" aria-live="polite">
      <svg class="mtr-star" viewBox="0 0 24 24" aria-hidden="true"><path :d="STAR" /></svg>
      <span class="mtr-t work">{{ heading }}</span>
    </div>
    <div v-if="chainVisible" class="mtr-b">
      <div class="mtr-i">
        <div class="mtr-l" role="list">
          <template v-for="item in chain" :key="item.key">
            <details v-if="item.type === 'group'" class="grp" role="listitem" :open="item.running || undefined">
              <summary class="mtr-row" :class="{ running: item.running }">
                <span class="mtr-mark" aria-hidden="true"><span class="mtr-dot"></span></span>
                <span class="mtr-p">{{ item.label }}</span>
                <svg class="gchev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
                <span class="mtr-s">{{ secs(item.ms) }}</span>
              </summary>
              <div class="gmem">
                <div v-for="member in item.members" :key="member.key" class="mtr-row" :class="{ error: member.error, running: member.running }" role="listitem">
                  <span class="mtr-mark" aria-hidden="true"><span class="mtr-dot"></span></span>
                  <span class="mtr-p" :title="member.error || undefined">{{ member.label }}</span>
                  <span class="mtr-s">{{ secs(member.ms) }}</span>
                </div>
              </div>
            </details>
            <details v-else-if="item.type === 'think' && item.reasoning" class="grp mtr-row-think" role="listitem">
              <summary class="mtr-row think" :class="{ running: item.running }">
                <span class="mtr-mark" aria-hidden="true"><span class="mtr-dot"></span></span>
                <span class="mtr-p">{{ thinkLabel(item) }}</span>
                <svg class="gchev" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
              </summary>
              <div class="gmem mtr-reason" v-html="reasoningHtml"></div>
            </details>
            <div v-else class="mtr-row" :class="{ think: item.type === 'think', error: item.error, running: item.running }" role="listitem">
              <span class="mtr-mark" aria-hidden="true"><span class="mtr-dot"></span></span>
              <span class="mtr-p" :title="item.error || undefined">{{ itemLabel(item) }}</span>
              <span class="mtr-s">{{ item.ms ? secs(item.ms) : "" }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "../useI18n";
import { buildChain, buildLiveChain, secs, traceHeading } from "../composables/useMayaTrace";

const { t } = useI18n();

// The four-pointed star, lifted from GEO-CN. A spinner says "wait"; this says "she is working",
// which is the same fact with the customer's dignity intact.
const STAR = "M12 2.6l2.1 5.9 5.9 2.1-5.9 2.1L12 18.6l-2.1-5.9L4 10.6l5.9-2.1z";

const props = defineProps({
  // [{ call_id, state: 'running'|'done'|'error', label, label_done, kind, group_noun, ms,
  //    summary?, error? }] — newest last, in call order.
  rows: { type: Array, default: () => [] },
  live: { type: Boolean, default: false },
  // What the live line says while nothing is running yet ("Thinking…", "Running the diagnosis…").
  label: { type: String, default: "" },
  // True once the first answer token has arrived, so the live line stops claiming to look things up.
  writing: { type: Boolean, default: false },
  // Model reasoning summary, folded into the one Thinking row instead of its own drawer.
  reasoning: { type: String, default: "" },
  // Epoch ms. `startedAt` is when the turn began (the server's clock, via the `status` event);
  // `answerAt` is when the first answer token landed. Both may be 0 on a replayed history turn.
  startedAt: { type: Number, default: 0 },
  answerAt: { type: Number, default: 0 },
  // A replayed turn has no clock at all: the durations it shows are the ones that were stored.
  totalMs: { type: Number, default: 0 },
  writeMs: { type: Number, default: 0 },
  // The server's own measurement of the gaps between tool calls. `null` (the default, and what a
  // turn stored before R16b has) means "work it out from the other three".
  thinkMs: { type: Number, default: null },
  // Passed through so the parent can render markdown with its own helper.
  renderReasoning: { type: Function, default: null },
});

const open = ref(false);
// One 250ms tick drives every clock in here, and it only exists while the turn is actually running.
const tick = ref(0);
let timer = null;
const settledTotal = ref(0);
const settledWrite = ref(0);
// The instant this turn stopped. Every duration in the chain is read against it afterwards, so a
// tab left open does not keep "Wrote the answer · 17s" counting up.
const settledAt = ref(0);

function stopTimer() {
  if (timer) { clearInterval(timer); timer = null; }
}
watch(
  () => props.live,
  (isLive) => {
    if (isLive) {
      settledTotal.value = 0;
      settledWrite.value = 0;
      settledAt.value = 0;
      // PT, 2026-09-13: the chain is the thing you watch while you wait, so it starts open and
      // stays open when the turn settles. Only a replayed turn starts folded.
      open.value = true;
      if (!timer) timer = setInterval(() => { tick.value += 1; }, 250);
      return;
    }
    stopTimer();
    settledAt.value = Date.now();
    // Freeze the clock at the moment the turn settled: re-reading Date.now() afterwards would make
    // "Thought for 42s" keep growing for as long as the customer leaves the tab open.
    if (props.startedAt) settledTotal.value = Math.max(0, Date.now() - props.startedAt);
    if (props.answerAt) settledWrite.value = Math.max(0, Date.now() - props.answerAt);
  },
  { immediate: true },
);
onBeforeUnmount(stopTimer);

const totalMs = computed(() => settledTotal.value || props.totalMs || 0);
const writeMs = computed(() => settledWrite.value || props.writeMs || 0);

// While the turn runs the chain is only worth opening once there is a step in it: a zero-tool turn
// (most of production) stays the one shimmering line it has always been, and the header is the
// only thing that ever says "Thinking…" for it.
const chainVisible = computed(() => (props.live ? props.rows.length > 0 : true));

// A live turn always shows its line; a settled one only if it has something to report.
const heading = computed(() => {
  void tick.value;
  return traceHeading({
    rows: props.rows,
    live: props.live,
    liveLabel: props.label || t("maya_trace_thinking"),
    writing: props.writing,
    chainOpen: props.live && chainVisible.value,
    totalMs: totalMs.value,
    copy: {
      writing: t("maya_trace_writing"),
      thought: t("maya_trace_thought"),
      lookup: t("maya_trace_lookup"),
      lookups: t("maya_trace_lookups"),
    },
  });
});
// Live: one line, until the answer starts arriving with no lookups behind it — then nothing, so a
// zero-tool turn leaves no residue at all rather than a "Thought for 3s · 0 lookups" epitaph.
const visible = computed(() => {
  if (!props.live) return heading.value !== null;
  if (props.writing && !props.rows.length) return false;
  return Boolean(heading.value);
});

// A turn this browser watched happen carries a clock: `startedAt` from the gateway's `status`
// frame and a local stamp on every row as its `running` frame landed. That is enough to lay the
// chain out in time, live and after it settles. A turn read back out of history carries durations
// and nothing else, so it gets the summed chain instead.
const watched = computed(() => props.startedAt > 0 && (props.live || props.rows.some((row) => row.startedAt)));

const chain = computed(() => {
  void tick.value;
  if (watched.value) {
    return buildLiveChain({
      rows: props.rows,
      startedAt: props.startedAt,
      answerAt: props.answerAt,
      now: props.live ? Date.now() : settledAt.value || Date.now(),
      settled: !props.live,
      reasoning: props.reasoning,
    });
  }
  return buildChain({
    rows: props.rows,
    totalMs: totalMs.value,
    writeMs: writeMs.value,
    reasoning: props.reasoning,
    settled: true,
    thinkMs: props.thinkMs,
  });
});

const reasoningHtml = computed(() =>
  props.renderReasoning ? props.renderReasoning(props.reasoning || "") : escapeText(props.reasoning || ""),
);

function escapeText(text) {
  return String(text).replace(/[&<>]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[ch]);
}
function thinkLabel(item) {
  return item.ms ? t("maya_trace_thinking_row") + " · " + secs(item.ms) : t("maya_trace_thinking_row");
}
function itemLabel(item) {
  if (item.type === "think") return t("maya_trace_thinking_row");
  // Present tense while the words are still arriving, past tense the moment they stop.
  if (item.type === "write") return item.running ? t("maya_trace_writing") : t("maya_trace_wrote");
  return item.label;
}
</script>

<style>
/* Ported from `maya_trace_variants.html` variant A, whose `:root` was itself lifted from
   www/assets/css/app.css — so every colour here is a token the sheet already defines and dark mode
   comes for free. Height animates via grid-template-rows 0fr→1fr; no JS measures anything. */
/* No --mgut here. That variable is the avatar gutter for things the legacy markup drops straight
   into the lane (.mtoolev and friends); this component renders INSIDE .mcol, which is already past
   the avatar, so applying it again indented the whole trace 44px to the right of the bubble it
   belongs to. Flush with the bubble is what the approved mockup shows. */
.mtrace { margin: 0 0 8px; max-width: 100%; }
.mtr-h { display: flex; align-items: center; gap: 7px; padding: 2px 0; border: none; background: none; cursor: pointer; font: inherit; font-size: var(--fs-caption); color: var(--mute); text-align: left; width: 100%; }
.mtr-h-live { cursor: default; }
.mtr-h:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; border-radius: 4px; }
.mtr-star { flex: 0 0 auto; width: 13px; height: 13px; fill: var(--mute); opacity: .85; transition: opacity .3s; }
.mtrace.live .mtr-star { fill: var(--indigo); opacity: 1; animation: mtr-star-pulse 1.4s cubic-bezier(.23, 1, .32, 1) infinite; }
@keyframes mtr-star-pulse { 0%, 100% { opacity: .55; transform: scale(.92); } 50% { opacity: 1; transform: scale(1); } }
.mtr-chev { width: 13px; height: 13px; flex: 0 0 auto; fill: none; stroke: var(--mute); stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; transition: transform .3s cubic-bezier(.23, 1, .32, 1); margin-left: auto; }
.mtrace.on .mtr-chev { transform: rotate(180deg); }
.mtr-t { font-weight: 600; letter-spacing: .01em; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Working: only the WORDS shimmer. A block-level shimmer steals the eye from the answer. */
.mtr-t.work { background-image: linear-gradient(90deg, var(--mute) 35%, var(--ink) 50%, var(--mute) 65%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: mtr-shimmer 1.4s linear infinite; }
.mtr-t.done { color: var(--mute); animation: mtr-fade .3s ease both; }
@keyframes mtr-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes mtr-fade { from { opacity: 0; } to { opacity: 1; } }
.mtr-b { display: grid; grid-template-rows: 0fr; opacity: 0; transition: grid-template-rows .4s cubic-bezier(.23, 1, .32, 1), opacity .4s; }
.mtrace.on .mtr-b { grid-template-rows: 1fr; opacity: 1; }
.mtr-i { overflow: hidden; }
/* The rail draws only under an OPEN disclosure — it means "this belongs to the row above", which
   is only true when there IS content. */
.mtr-l { border-left: 1px solid var(--line); padding-left: 16px; margin: 6px 0 2px 6px; }
.mtr-row { display: flex; align-items: flex-start; gap: 8px; min-height: 26px; font-size: var(--fs-caption); color: var(--mute); animation: mtr-up .32s cubic-bezier(.23, 1, .32, 1) both; }
@keyframes mtr-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.mtr-mark { flex: 0 0 auto; width: 12px; height: 12px; margin-top: 6px; display: flex; align-items: center; justify-content: center; }
.mtr-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--mute); opacity: .7; }
.mtr-p { flex: 0 1 auto; min-width: 0; line-height: 1.5; padding-top: 4px; overflow-wrap: anywhere; color: var(--ink); opacity: .8; }
/* 0 0 auto and no margin-left:auto — the duration sits right after the words instead of being
   pushed to the far edge of a full-width bubble. */
.mtr-s { flex: 0 0 auto; padding-top: 4px; font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--fs-caption2); color: var(--mute); opacity: .8; }
.mtr-row.think .mtr-p { opacity: .62; font-style: italic; }
/* The step that is happening right now: the same word-level shimmer the header uses, so the eye
   can find the present without a second kind of spinner. Its duration keeps counting either way —
   the number is JS, not animation. */
.mtr-row.running .mtr-p { background-image: linear-gradient(90deg, var(--mute) 35%, var(--ink) 50%, var(--mute) 65%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; opacity: 1; animation: mtr-shimmer 1.4s linear infinite; }
.mtr-row.running.think .mtr-p { opacity: 1; }
.mtr-row.running .mtr-dot { background: var(--indigo); opacity: 1; animation: mtr-star-pulse 1.4s cubic-bezier(.23, 1, .32, 1) infinite; }
.mtr-row.error .mtr-p { text-decoration: line-through; text-decoration-color: var(--line); }
/* A nested group: one row that opens to its members, same shape one level in. */
.mtrace .grp { width: 100%; }
.mtrace .grp > summary { list-style: none; cursor: pointer; display: flex; align-items: flex-start; gap: 8px; min-height: 26px; }
.mtrace .grp > summary::-webkit-details-marker { display: none; }
.mtrace .grp > summary:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; border-radius: 4px; }
.mtrace .gchev { width: 11px; height: 11px; flex: 0 0 auto; margin: 8px 0 0 2px; fill: none; stroke: var(--mute); stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; transition: transform .25s cubic-bezier(.23, 1, .32, 1); }
.mtrace .grp[open] .gchev { transform: rotate(180deg); }
.mtrace .gmem { border-left: 1px solid var(--line); padding-left: 14px; margin: 2px 0 4px 5px; }
.mtrace .gmem .mtr-row { min-height: 24px; }
/* Reasoning: a height cap plus a fade, never a "show more" button. */
.mtrace .mtr-reason { max-height: 220px; overflow-y: auto; padding-top: 2px; padding-bottom: 4px; }
/* mdLite hands back the site's own markup, which is sized for a bubble; inside the chain the
   reasoning is the least of the work and has to read that way. */
.mtrace .mtr-reason, .mtrace .mtr-reason * { font-size: var(--fs-caption2); line-height: 1.6; color: var(--mute); font-style: italic; }
.mtrace .mtr-reason p + p { margin-top: 6px; }
@media (prefers-reduced-motion: reduce) {
  .mtrace .mtr-b, .mtrace .mtr-chev, .mtrace .gchev { transition-duration: .01ms; }
  .mtr-t.work, .mtr-t.done, .mtr-row, .mtrace.live .mtr-star, .mtr-row.running .mtr-p, .mtr-row.running .mtr-dot { animation: none; }
  /* The timers keep running — only their shimmer stops. */
  .mtr-t.work, .mtr-row.running .mtr-p { background-image: none; color: var(--mute); -webkit-text-fill-color: currentColor; opacity: .75; }
}
</style>
